import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Flag, Clock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { checkRankUp } from '../utils/points';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ==================== ENARM HEADER ====================
const ENARMHeader = ({ timeRemaining, onFinish }) => {
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const isLow = timeRemaining < 600; // Less than 10 min

  return (
    <div className="bg-[#1a5276] text-white px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between" data-testid="enarm-header">
      <div className="flex items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-[#1a5276] font-bold text-sm sm:text-lg">E</span>
          </div>
          <div className="hidden sm:block">
            <h1 className="text-2xl font-extrabold tracking-wide leading-tight">ENARM</h1>
            <p className="text-sm text-blue-200 leading-tight font-medium">EXAMEN NACIONAL DE ASPIRANTES<br/>A RESIDENCIAS MÉDICAS</p>
          </div>
          <div className="sm:hidden">
            <h1 className="text-base font-extrabold tracking-wide leading-tight">ENARM</h1>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-4">
        <div className="text-right">
          <span className="text-[10px] sm:text-sm text-blue-200 hidden sm:block">Tiempo restante:</span>
          <div className={`text-lg sm:text-3xl font-mono font-bold tabular-nums ${isLow ? 'text-red-400 animate-pulse' : 'text-white'}`} data-testid="exam-timer">
            {formatTime(timeRemaining)}
          </div>
        </div>
        <button
          onClick={onFinish}
          data-testid="finish-exam-button"
          className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-5 py-1.5 sm:py-2.5 rounded font-semibold text-xs sm:text-base transition-colors"
        >
          Finalizar
        </button>
      </div>
    </div>
  );
};

// ==================== QUESTION GRID ====================
const QuestionGrid = ({ totalQuestions, questions, answers, marked, currentIndex, onNavigate, onClose }) => {
  return (
    <div className="bg-gray-100 border border-gray-300 rounded p-2" data-testid="question-grid">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-700 text-center flex-1">Preguntas</h3>
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-gray-500 hover:text-gray-800 p-1">
            <XCircle size={20} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-10 sm:grid-cols-10 lg:grid-cols-5 gap-[3px]">
        {Array.from({ length: totalQuestions }, (_, i) => {
          const q = questions[i];
          const origIdx = q ? String(q.index) : String(i);
          const isAnswered = answers[origIdx] !== undefined;
          const isMarked = marked.includes(i);
          const isCurrent = i === currentIndex;

          let bg = 'bg-white text-gray-700 border-gray-300';
          if (isAnswered) bg = 'bg-green-500 text-white border-green-600';
          if (isMarked && !isAnswered) bg = 'bg-yellow-400 text-gray-900 border-yellow-500';
          if (isMarked && isAnswered) bg = 'bg-green-500 text-white border-yellow-400 ring-1 ring-yellow-400';
          if (isCurrent) bg += ' ring-2 ring-blue-600';

          return (
            <button
              key={i}
              onClick={() => { onNavigate(i); onClose && onClose(); }}
              className={`w-6 h-6 text-[10px] font-bold rounded border ${bg} hover:opacity-80 transition-all`}
              data-testid={`question-nav-${i}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      <div className="mt-2 space-y-0.5 text-[9px] text-gray-500">
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-green-500 rounded"></div> Contestada</div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-yellow-400 rounded"></div> Marcada</div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-white border border-gray-300 rounded"></div> Sin contestar</div>
      </div>
    </div>
  );
};

// ==================== RESULTS VIEW ====================
const ResultsView = ({ results, simulacroTitle, onBack }) => {
  const [expandedEsp, setExpandedEsp] = useState({});
  
  const percentage = results.score_percentage;
  const passed = percentage >= 60;
  let color = 'text-red-600';
  if (percentage >= 80) color = 'text-green-600';
  else if (percentage >= 60) color = 'text-yellow-600';

  // Sort especialidades by total questions desc
  const sortedEsp = Object.entries(results.by_especialidad)
    .sort((a, b) => b[1].total - a[1].total);

  // Group temas by especialidad
  const temasByEsp = {};
  for (const t of results.by_tema) {
    if (!temasByEsp[t.especialidad]) temasByEsp[t.especialidad] = [];
    temasByEsp[t.especialidad].push(t);
  }

  const toggleEsp = (esp) => {
    setExpandedEsp(prev => ({ ...prev, [esp]: !prev[esp] }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#1a5276] text-white px-6 py-4">
        <h1 className="text-xl font-bold">Resultados del Simulacro</h1>
        <p className="text-blue-200 text-sm">{simulacroTitle}</p>
      </div>
      <div className="max-w-4xl mx-auto p-6">
        {/* Overall Score */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center mb-8" data-testid="overall-score">
          <h2 className="text-lg text-gray-600 mb-2">Calificación Final</h2>
          <div className={`text-6xl font-bold ${color}`}>{percentage}%</div>
          <p className="text-gray-500 mt-2">{results.correct_answers} aciertos de {results.total_questions} preguntas</p>
        </div>

        {!passed ? (
          /* Not passed - show message, hide desglose */
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center mb-8" data-testid="not-passed-message">
            <XCircle size={48} className="mx-auto text-red-400 mb-4" />
            <h3 className="text-xl font-bold text-red-700 mb-2">No aprobaste</h3>
            <p className="text-red-600 mb-1">Tu calificación: <strong>{percentage}%</strong></p>
            <p className="text-gray-600">Necesitas <strong>60%</strong> o más para ver el desglose por especialidad y subtemas.</p>
            <p className="text-gray-500 mt-4 text-sm">Intenta de nuevo para mejorar tu resultado.</p>
          </div>
        ) : (
          /* Passed - show full desglose */
          <>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Desglose por Especialidad</h3>
            <p className="text-sm text-gray-500 mb-4">Haz clic en cada especialidad para ver el desglose por subtemas</p>
            <div className="space-y-3 mb-8">
          {sortedEsp.map(([esp, data]) => {
            const isExpanded = expandedEsp[esp];
            const temas = (temasByEsp[esp] || []).sort((a, b) => b.total - a.total);
            
            return (
              <div key={esp} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button 
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  onClick={() => toggleEsp(esp)}
                  data-testid={`esp-toggle-${esp}`}
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight 
                      size={20} 
                      className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} 
                    />
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900">{esp}</h4>
                      <p className="text-sm text-gray-500">{data.correct}/{data.total} aciertos</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${data.percentage >= 80 ? 'text-green-600' : data.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {data.percentage}%
                    </span>
                    <span className="text-xs text-[#1a5276] font-medium bg-blue-50 px-2 py-1 rounded">
                      {temas.length} subtemas
                    </span>
                  </div>
                </button>
                {/* Tema detail */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-6 py-3">
                    <p className="text-xs text-gray-500 font-medium uppercase mb-2">Subtemas</p>
                    {temas.map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <span className="text-sm text-gray-700">{t.tema}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">{t.correct}/{t.total}</span>
                          <span className={`text-sm font-bold min-w-[3rem] text-right ${t.percentage >= 80 ? 'text-green-600' : t.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {t.percentage}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
          </>
        )}

        <button
          onClick={onBack}
          data-testid="back-to-simulacros"
          className="w-full py-3 bg-[#1a5276] text-white font-semibold rounded-lg hover:bg-[#154360] transition-colors"
        >
          Volver a Simulacros
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN EXAM COMPONENT ====================
const SimulacroExam = ({ user, onLogout }) => {
  const { simulacroId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const viewResultsMode = searchParams.get('mode') === 'results';

  const [simulacro, setSimulacro] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(5 * 3600);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [showConfirmFinish, setShowConfirmFinish] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const answersRef = useRef(answers);
  const timerRef = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  // Load simulacro and start/resume attempt
  useEffect(() => {
    const load = async () => {
      try {
        if (viewResultsMode) {
          // Just fetch results for the completed attempt
          const simRes = await apiClient.get(`/simulacros/${simulacroId}`);
          setSimulacro(simRes.data);
          
          const attemptsRes = await apiClient.get(`/simulacros/${simulacroId}/latest-result`);
          if (attemptsRes.data && attemptsRes.data.results) {
            setResults(attemptsRes.data.results);
          } else {
            toast.error('No hay resultados disponibles');
            navigate('/simulacros');
          }
        } else {
          // Start or resume exam
          const [simRes, attemptRes] = await Promise.all([
            apiClient.get(`/simulacros/${simulacroId}`),
            apiClient.post(`/simulacros/${simulacroId}/start`)
          ]);
          
          const simData = simRes.data;
          const attemptData = attemptRes.data;
          
          // Reorder questions based on attempt's question_order
          const questionOrder = attemptData.question_order;
          if (questionOrder && questionOrder.length > 0) {
            const questionsByIndex = {};
            for (const q of simData.questions) {
              questionsByIndex[q.index] = q;
            }
            const reordered = questionOrder.map((origIdx, newIdx) => ({
              ...questionsByIndex[origIdx],
              display_index: newIdx
            }));
            simData.questions = reordered;
          }
          
          setSimulacro(simData);
          setAttempt(attemptData);

          // Resume in-progress attempt
          setAnswers(attemptData.answers || {});
          setMarked(attemptData.marked || []);
          
          // Calculate remaining time (ensure UTC parsing)
          let startedStr = attemptData.started_at;
          if (startedStr && !startedStr.endsWith('Z') && !startedStr.includes('+')) {
            startedStr += 'Z';
          }
          const started = new Date(startedStr);
          const elapsed = Math.floor((Date.now() - started.getTime()) / 1000);
          const remaining = Math.max(0, attemptData.time_limit_seconds - elapsed);
          setTimeRemaining(remaining);
          
          if (remaining <= 0) {
            handleFinish(attemptData.answers || {});
          }
        }
      } catch (err) {
        toast.error('Error al cargar simulacro');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
  }, [simulacroId]);

  // Timer countdown
  useEffect(() => {
    if (!attempt || attempt.status === 'completed' || results) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleFinish(answersRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto-save every 30 seconds
    saveTimerRef.current = setInterval(() => {
      apiClient.post(`/simulacros/${simulacroId}/save`, {
        answers: answersRef.current,
        marked
      }).catch(() => {});
    }, 30000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
  }, [attempt, results]);

  const handleAnswer = (option) => {
    // Use the original question index for storing answers
    const currentQ = simulacro.questions[currentIndex];
    const answerKey = String(currentQ.index);
    const currentAnswer = answers[answerKey];
    if (currentAnswer === option) {
      // Deselect: remove answer, question goes back to white
      const newAnswers = { ...answers };
      delete newAnswers[answerKey];
      setAnswers(newAnswers);
    } else {
      const newAnswers = { ...answers, [answerKey]: option };
      setAnswers(newAnswers);
    }
  };

  const handleMark = () => {
    setMarked(prev =>
      prev.includes(currentIndex) ? prev.filter(i => i !== currentIndex) : [...prev, currentIndex]
    );
  };

  const handleNavigate = (index) => {
    if (index >= 0 && index < (simulacro?.questions?.length || 0)) {
      setCurrentIndex(index);
    }
  };

  const handleFinish = async (finalAnswers) => {
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
      const res = await apiClient.post(`/simulacros/${simulacroId}/finish`, {
        answers: finalAnswers || answers
      });
      setResults(res.data);
      checkRankUp(res.data);
      setShowConfirmFinish(false);
    } catch (err) {
      toast.error('Error al finalizar simulacro');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-3 border-gray-300 border-t-[#1a5276] rounded-full"></div>
      </div>
    );
  }

  if (results) {
    return <ResultsView results={results} simulacroTitle={simulacro?.title || 'Simulacro'} onBack={() => navigate('/simulacros')} />;
  }

  if (!simulacro) return null;

  const questions = simulacro.questions;
  const currentQ = questions[currentIndex];
  const options = ['A', 'B', 'C', 'D'].filter(o => {
    const val = currentQ[`option_${o.toLowerCase()}`];
    return val && val.trim();
  });

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="simulacro-exam">
      {/* ENARM Header */}
      <ENARMHeader timeRemaining={timeRemaining} onFinish={() => setShowConfirmFinish(true)} />

      {/* Mobile: Grid toggle bar */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">
          Pregunta {currentIndex + 1}/{questions.length} | Contestadas: {Object.keys(answers).length}
        </span>
        <button
          onClick={() => setShowGrid(true)}
          data-testid="toggle-grid-button"
          className="flex items-center gap-1 px-3 py-1 bg-[#1a5276] text-white text-xs font-semibold rounded"
        >
          <Flag size={12} /> Ver Preguntas
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row p-2 sm:p-3 gap-2 sm:gap-3 max-w-[1400px] mx-auto w-full overflow-hidden">
        {/* Left: Case + Question */}
        <div className="flex-1 bg-white rounded border border-gray-300 p-4 sm:p-8 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {/* Case Text */}
          {currentQ.case_text && (
            <div className="mb-4 sm:mb-6">
              <h2 className="text-[#1a5276] font-bold text-base sm:text-xl mb-2 sm:mb-3">Caso Clínico</h2>
              <p className="text-gray-800 text-sm sm:text-base leading-relaxed">{currentQ.case_text}</p>
            </div>
          )}

          {/* Question */}
          <div className="mb-4 sm:mb-6">
            <p className="text-[#2e7d32] font-semibold text-base sm:text-lg italic mb-3 sm:mb-5" data-testid="question-text">
              {currentQ.question_text}
            </p>

            {/* Options */}
            <div className="space-y-2 sm:space-y-3">
              {options.map(o => {
                const optionText = currentQ[`option_${o.toLowerCase()}`];
                const answerKey = String(currentQ.index);
                const isSelected = answers[answerKey] === o;
                return (
                  <label
                    key={o}
                    className={`flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected ? 'border-[#1a5276] bg-blue-50' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    data-testid={`option-${o}`}
                    onClick={(e) => { e.preventDefault(); handleAnswer(o); }}
                  >
                    <input
                      type="radio"
                      name={`q-${currentIndex}`}
                      checked={isSelected}
                      readOnly
                      className="mt-0.5 w-4 h-4 sm:w-5 sm:h-5 accent-[#1a5276] flex-shrink-0"
                    />
                    <span className="text-gray-800 text-sm sm:text-base">
                      <strong className="mr-1">{o})</strong> {optionText}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Question info */}
          <div className="text-xs sm:text-sm text-gray-400 mt-4">
            Pregunta {currentIndex + 1} de {questions.length}
            {currentQ.case_number && ` | Caso ${currentQ.case_number}`}
          </div>
        </div>

        {/* Right: Question Grid - desktop only (visible always) */}
        <div className="hidden lg:block lg:w-44 flex-shrink-0" style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
          <QuestionGrid
            totalQuestions={questions.length}
            questions={questions}
            answers={answers}
            marked={marked}
            currentIndex={currentIndex}
            onNavigate={handleNavigate}
          />
        </div>
      </div>

      {/* Mobile: Grid overlay */}
      {showGrid && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden flex justify-end" onClick={() => setShowGrid(false)}>
          <div
            className="bg-white w-[85%] max-w-sm h-full overflow-y-auto p-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <QuestionGrid
              totalQuestions={questions.length}
              questions={questions}
              answers={answers}
              marked={marked}
              currentIndex={currentIndex}
              onNavigate={handleNavigate}
              onClose={() => setShowGrid(false)}
            />
          </div>
        </div>
      )}

      {/* Bottom Buttons */}
      <div className="bg-white border-t border-gray-300 px-2 sm:px-6 py-2 sm:py-3 flex items-center justify-center gap-1.5 sm:gap-3" data-testid="exam-controls">
        <button
          onClick={() => handleNavigate(currentIndex - 1)}
          disabled={currentIndex === 0}
          data-testid="prev-button"
          className="flex items-center gap-1 px-3 sm:px-6 py-2 sm:py-2.5 bg-[#1a5276] text-white font-semibold rounded text-xs sm:text-base hover:bg-[#154360] disabled:opacity-30 transition-colors"
        >
          <ChevronLeft size={16} /> <span className="hidden sm:inline">Anterior</span><span className="sm:hidden">Ant</span>
        </button>
        <button
          onClick={handleMark}
          data-testid="mark-button"
          className={`flex items-center gap-1 px-3 sm:px-6 py-2 sm:py-2.5 font-semibold rounded text-xs sm:text-base transition-colors ${
            marked.includes(currentIndex) 
              ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-500' 
              : 'bg-yellow-300 text-gray-800 hover:bg-yellow-400'
          }`}
        >
          <Flag size={14} /> <span className="hidden sm:inline">{marked.includes(currentIndex) ? 'Desmarcar' : 'Marcar'}</span><span className="sm:hidden">Marc</span>
        </button>
        <button
          onClick={() => handleNavigate(currentIndex + 1)}
          disabled={currentIndex === questions.length - 1}
          data-testid="next-button"
          className="flex items-center gap-1 px-3 sm:px-6 py-2 sm:py-2.5 bg-[#2196F3] text-white font-semibold rounded text-xs sm:text-base hover:bg-[#1976D2] disabled:opacity-30 transition-colors"
        >
          <span className="hidden sm:inline">Siguiente</span><span className="sm:hidden">Sig</span> <ChevronRight size={16} />
        </button>
        <button
          onClick={() => setShowConfirmFinish(true)}
          data-testid="finish-button-bottom"
          className="flex items-center gap-1 px-3 sm:px-6 py-2 sm:py-2.5 bg-red-600 text-white font-semibold rounded text-xs sm:text-base hover:bg-red-700 transition-colors"
        >
          <span className="hidden sm:inline">Finalizar</span><span className="sm:hidden">Fin</span>
        </button>
      </div>

      {/* Confirm Finish Dialog */}
      {showConfirmFinish && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="confirm-finish-dialog">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-3">¿Finalizar simulacro?</h3>
            <p className="text-gray-600 mb-2">
              Has contestado <strong>{Object.keys(answers).length}</strong> de <strong>{questions.length}</strong> preguntas.
            </p>
            {Object.keys(answers).length < questions.length && (
              <p className="text-yellow-700 bg-yellow-50 p-2 rounded text-sm mb-4">
                Tienes {questions.length - Object.keys(answers).length} preguntas sin contestar.
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConfirmFinish(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleFinish()}
                data-testid="confirm-finish-yes"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Sí, finalizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulacroExam;
